import React from 'react';
import DynamicProfileImage from 'components/commons/DynamicProfileImage';
import styled from 'styled-components';
import { MapPin, Star } from 'lucide-react';
import colors from 'theme/colors';
import { useNavigate } from 'react-router-dom';
import { ui, placeIsOpen, placeRating, PLACEHOLDER_EVENT } from 'theme/discover';
import {
    CompactCard,
    CompactThumb,
    CompactBadge,
    CompactBody,
    CompactCategory,
    CompactTitle,
    CompactMeta,
    CompactFooter,
} from 'components/commons/CompactCard';
// TODO: Descomentar quando o endpoint de favoritar estabelecimentos estiver pronto
// import useFavorite from 'hooks/useFavorite';

/**
 * Cartão de lugar no desenho do app (WUICompactCard), irmão do EventCardV2:
 * foto com o selo de aberto/fechado e o logo do lugar, categoria, nome, bairro
 * e a nota fechando o cartão.
 */
export default function EstablishmentCardV2({
    data,
    onCardClick,
}) {
    const navigate = useNavigate();

    const href = data?.slug ? `/estabelecimento/${data.slug}/detalhes` : undefined;
    const isOpen = placeIsOpen(data);
    const rating = placeRating(data);
    // A categoria já está na linha de cima; aqui vai só onde fica.
    const area = data?.address?.district || data?.address?.name || data?.address?.city;

    // Link de verdade, como o cartão de evento: Ctrl/Cmd/clique-do-meio abrem
    // em nova aba; só o clique simples passa pelo `onCardClick`.
    function handleClick(e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
            return;
        }

        e.preventDefault();

        if (onCardClick) {
            onCardClick(data);
        } else if (href) {
            navigate(href);
        }
    }

    return (
        <CompactCard as={href ? 'a' : 'div'} href={href} onClick={handleClick}>
            <CompactThumb>
                <img
                    src={data?.backgroundImage?.path || PLACEHOLDER_EVENT}
                    alt={data?.name ? `Foto de ${data.name}` : 'Foto do lugar'}
                    loading='lazy'
                    onError={(e) => { e.target.onerror = null; e.target.src = PLACEHOLDER_EVENT; }}
                />

                <CompactBadge>
                    <OpenDot $open={isOpen} />
                    {isOpen ? 'Aberto agora' : 'Fechado'}
                </CompactBadge>

                <Logo>
                    <DynamicProfileImage
                        img={data?.profileImage?.thumb || data?.profileImage?.path}
                        word={data?.name}
                        size='36px'
                        textSize='normal'
                    />
                </Logo>
            </CompactThumb>

            <CompactBody>
                {!!data?.tagsEstablishments?.[0]?.name && (
                    <CompactCategory>{data.tagsEstablishments[0].name}</CompactCategory>
                )}

                <CompactTitle>{data?.name}</CompactTitle>

                {!!area && (
                    <CompactMeta>
                        <MapPin size={12} />
                        <span>{area}</span>
                    </CompactMeta>
                )}

                {rating ? (
                    <CompactFooter>
                        <Star size={13} color='#F5A524' fill='#F5A524' />
                        {rating}
                        <RatingCount>({data.rating.count})</RatingCount>
                    </CompactFooter>
                ) : (
                    <CompactFooter as='span' style={{ color: ui.inkFaint, fontWeight: 600, fontSize: 12 }}>
                        Ainda sem avaliações
                    </CompactFooter>
                )}
            </CompactBody>
        </CompactCard>
    );
}

const OpenDot = styled.span`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ $open }) => $open ? colors.success : colors.danger};
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
`;

// O logo do lugar na borda de baixo da foto, com o anel branco do cartão.
const Logo = styled.span`
    position: absolute;
    left: 10px;
    bottom: 10px;
    display: flex;
    border-radius: 50%;
    box-shadow: 0 0 0 2px ${colors.neutralLight}, 0 2px 8px rgba(10, 5, 25, 0.2);
`;

const RatingCount = styled.span`
    font-weight: 500;
    color: ${ui.inkFaint};
`;
